import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import { State } from '../../models/state'
import { RoutePaths } from '../../routes'
import { StateService } from '../../services/state-services'

interface AccountFormProps {
  onSubmit: (values: any) => void
  mode: 'edit' | 'create'
  form: any
}

const AccountForm: React.FC<AccountFormProps> = ({ onSubmit, mode, form }) => {
  const { register, handleSubmit, formState, getValues, setValue } = form
  const [states, setStates] = useState<State[]>([])

  useEffect(() => {
    ;(async () => setStates((await StateService.getActiveStates()) || []))()
  }, [])

  useEffect(() => {
    if (mode === 'edit' && states.length > 0) {
      const defaultState = getValues('state')
      setValue('state', defaultState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states])

  return (
    <form
      className="container mx-auto max-w-[600px] mt-[60px] mb-[100px] flex flex-col gap-[30px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex gap-5">
        <div className="flex flex-col w-1/2">
          <label
            htmlFor="firstName"
            className="uppercase font-medium tracking-widest mb-[10px]"
          >
            First Name
          </label>
          <input
            {...register('firstName')}
            id="firstName"
            type="text"
            name="firstName"
            placeholder="First Name"
            className={classNames(
              'px-[15px] py-3 border rounded-md border-primary-blue',
              {
                'border-red-500 focus:outline-red-500':
                  formState?.errors?.firstName,
              }
            )}
          />
          <div className="mt-1 text-sm text-red-500">
            {formState?.errors?.firstName?.message}
          </div>
        </div>
        <div className="flex flex-col w-1/2">
          <label
            htmlFor="lastName"
            className="uppercase font-medium tracking-widest mb-[10px]"
          >
            Last Name
          </label>
          <input
            {...register('lastName')}
            id="lastName"
            type="text"
            name="lastName"
            placeholder="Last Name"
            className={classNames(
              'px-[15px] py-3 border rounded-md border-primary-blue',
              {
                'border-red-500 focus:outline-red-500':
                  formState?.errors?.lastName,
              }
            )}
          />
          <div className="mt-1 text-sm text-red-500">
            {formState?.errors?.lastName?.message}
          </div>
        </div>
      </div>
      <div className="w-full flex flex-col gap-[10px]">
        <label
          htmlFor="county"
          className="font-medium tracking-widest uppercase"
        >
          County
        </label>
        <input
          {...register('county')}
          id="county"
          name="county"
          type="text"
          placeholder="County"
          className={classNames(
            'px-[15px] py-3 w-full border rounded-md border-primary-blue',
            {
              'border-red-500 focus:outline-red-500': formState?.errors?.county,
            }
          )}
        />
        <div className="-mt-1 text-sm text-red-500">
          {formState?.errors?.county?.message}
        </div>
      </div>
      <div className="w-full flex flex-col gap-[10px]">
        <label
          htmlFor="municipality"
          className="font-medium tracking-widest uppercase"
        >
          Municipality
        </label>
        <input
          {...register('municipality')}
          id="municipality"
          name="municipality"
          type="text"
          placeholder="Municipality"
          className={classNames(
            'px-[15px] py-3 w-full border rounded-md border-primary-blue',
            {
              'border-red-500 focus:outline-red-500':
                formState?.errors?.municipality,
            }
          )}
        />
        <div className="-mt-1 text-sm text-red-500">
          {formState?.errors?.municipality?.message}
        </div>
      </div>
      <div className="w-full flex flex-col gap-[10px]">
        <label
          htmlFor="email"
          className="font-medium tracking-widest uppercase"
        >
          Email
        </label>
        <input
          {...register('email')}
          disabled={formState.isSubmitting || mode === 'edit'}
          id="email"
          name="email"
          type="text"
          autoComplete="off"
          placeholder="Email"
          className={classNames(
            'px-[15px] py-3 w-full border rounded-md border-primary-blue',
            {
              'border-red-500 focus:outline-red-500': formState?.errors?.email,
            }
          )}
        />
        <div className="-mt-1 text-sm text-red-500">
          {formState?.errors?.email?.email}
        </div>
      </div>
      {mode === 'create' && (
        <>
          <div className="w-full flex flex-col gap-[10px]">
            <label
              htmlFor="password"
              className="font-medium tracking-widest uppercase"
            >
              Password
            </label>
            <input
              {...register('password')}
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              placeholder="Create Password"
              className={classNames(
                'px-[15px] py-3 w-full border rounded-md border-primary-blue',
                {
                  'border-red-500 focus:outline-red-500':
                    formState?.errors?.password,
                }
              )}
            />
            <div className="-mt-1 text-sm text-red-500">
              {formState?.errors?.password?.message}
            </div>
            <input
              {...register('repeatPassword')}
              id="repeatPassword"
              name="repeatPassword"
              type="password"
              autoComplete="new-password"
              placeholder="Reenter Password"
              className={classNames(
                'px-[15px] py-3 w-full border rounded-md border-primary-blue mt-[5px]',
                {
                  'border-red-500 focus:outline-red-500':
                    formState?.errors?.repeatPassword,
                }
              )}
            />
            <div className="-mt-1 text-sm text-red-500">
              {formState?.errors?.repeatPassword?.message}
            </div>
          </div>
        </>
      )}
      <div className="w-full flex flex-col gap-[10px]">
        <label
          htmlFor="phone"
          className="font-medium tracking-widest uppercase"
        >
          Phone
        </label>
        <input
          {...register('phone')}
          id="phone"
          name="phone"
          type="tel"
          defaultValue="+1"
          placeholder="Phone"
          className={classNames(
            'px-[15px] py-3 w-full border rounded-md border-primary-blue',
            {
              'border-red-500 focus:outline-red-500': formState?.errors?.phone,
            }
          )}
        />
        <div className="-mt-1 text-sm text-red-500">
          {formState?.errors?.phone?.message}
        </div>
      </div>
      <div className="w-full flex flex-col gap-[10px]">
        <label
          htmlFor="streetAddress"
          className="font-medium tracking-widest uppercase"
        >
          Business Address
        </label>
        <input
          {...register('streetAddress')}
          id="streetAddress"
          name="streetAddress"
          type="text"
          placeholder="Street Address"
          className={classNames(
            'px-[15px] py-3 w-full border rounded-md border-primary-blue',
            {
              'border-red-500 focus:outline-red-500':
                formState?.errors?.streetAddress,
            }
          )}
        />
        <div className="-mt-1 text-sm text-red-500">
          {formState?.errors?.streetAddress?.message}
        </div>
        <input
          {...register('aptSuiteBuilding')}
          type="text"
          placeholder="Apt, Suite, Building"
          className="px-[15px] py-3 w-full border rounded-md border-primary-blue mt-[5px]"
        />
        <input
          {...register('city')}
          id="city"
          type="text"
          placeholder="City"
          className={classNames(
            'px-[15px] py-3 w-full border rounded-md border-primary-blue mt-[5px]',
            {
              'border-red-500 focus:outline-red-500': formState?.errors?.city,
            }
          )}
        />
        <div className="-mt-1 text-sm text-red-500">
          {formState?.errors?.city?.message}
        </div>
        <div className="flex gap-5 mt-[5px]">
          <div className="w-1/2">
            <select
              {...register('state', { valueAsNumber: true })}
              disabled={states.length === 0}
              defaultValue={'-1'}
              className={classNames(
                'px-[15px] py-3 w-full border rounded-md border-primary-blue hover:cursor-pointer',
                {
                  'border-red-500 focus:outline-red-500':
                    formState?.errors?.state,
                }
              )}
            >
              <option value="-1" disabled selected>
                State
              </option>
              {states.map((state) => (
                <option key={`state-${state.id}`} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>
            <div className="mt-1 text-sm text-red-500">
              {formState?.errors?.state?.message}
            </div>
          </div>
          <div className="w-1/2">
            <input
              {...register('zipCode')}
              id="zipCode"
              type="text"
              placeholder="Zip Code"
              className={classNames(
                'px-[15px] py-3 w-full border rounded-md border-primary-blue',
                {
                  'border-red-500 focus:outline-red-500':
                    formState?.errors?.zipCode,
                }
              )}
            />
            <div className="mt-1 text-sm text-red-500">
              {formState?.errors?.zipCode?.message}
            </div>
          </div>
        </div>
      </div>
      {mode === 'create' && (
        <>
          <div className="w-full flex flex-col gap-[15px]">
            <div
              className={classNames(
                'w-full border border-primary-blue flex p-[15px] rounded-md',
                {
                  'border-red-500': formState?.errors?.termsOfService,
                }
              )}
            >
              <div className="flex-grow-0 flex-shrink-0 basis-8">
                <input
                  {...register('termsOfService')}
                  id="checkbox"
                  type="checkbox"
                  className="w-6 h-6 mt-1 mr-3 border-primary-blue hover:cursor-pointer"
                />
              </div>
              <div className="font-normal">
                By clicking this check box I acknowledge that I have read and I
                agree to the{' '}
                <a
                  href="/terms-of-service"
                  className="font-semibold text-primary-blue"
                >
                  Terms of Service.
                </a>
              </div>
            </div>
            <div
              className={classNames(
                'w-full border border-primary-blue flex p-[15px] rounded-md',
                {
                  'border-red-500': formState?.errors?.acknowledged,
                }
              )}
            >
              <div className="flex-grow-0 flex-shrink-0 basis-8">
                <input
                  {...register('acknowledged')}
                  id="acknowledged"
                  type="checkbox"
                  className="w-6 h-6 mt-1 mr-3 border-primary-blue hover:cursor-pointer"
                />
              </div>
              <div className="font-normal">
                By clicking this check box I acknowledge that I am a government
                entity and I consent to be invoiced.
              </div>
            </div>
          </div>
        </>
      )}
      <div className="w-full flex justify-end gap-[15px]">
        <a
          className="uppercase tracking-[0.07rem] px-6 py-3 text-primary-blue border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-transparent hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          href={RoutePaths.LANDING}
        >
          Cancel
        </a>
        <button
          type="submit"
          disabled={
            (formState.isSubmitting || !formState.isValid) &&
            formState.isSubmitted
          }
          className={classNames(
            'uppercase inline-flex tracking-[0.07rem] justify-center items-center px-6 py-3 text-white border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500',
            {
              'opacity-40 cursor-not-allowed':
                (formState.isSubmitting || !formState.isValid) &&
                formState.isSubmitted,
            }
          )}
        >
          {mode} Account
        </button>
      </div>
    </form>
  )
}

export default AccountForm
